import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AnimationController } from '@ionic/angular';
import { AsyncQueue, createAsyncQueue } from '@timechef/core/utils/async-queue';

@Component({
  selector: 'app-content-loader',
  templateUrl: './content-loader.component.html',
  styleUrls: ['./content-loader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContentLoaderComponent implements OnChanges {
  @Input() loaderState: { loading: boolean; error: any } | null = null;

  @ViewChild('loaderContainer') loaderEl: ElementRef | undefined;
  @ViewChild('contentContainer') contentEl: ElementRef | undefined;

  @HostBinding('class.limit-height') public shouldLimitMaxHeight = true;

  promise: Promise<void> | null = null;
  queue: AsyncQueue<void>;

  constructor(
    private animation: AnimationController,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
  ) {
    this.queue = createAsyncQueue({ dedupeConcurrent: true });
  }

  async ngOnChanges(changes: SimpleChanges) {
    const stateChanges = changes['loaderState'];
    const prev: boolean = stateChanges.previousValue?.loading;
    const curr: boolean = stateChanges.currentValue?.loading;

    if (stateChanges.firstChange) {
      setTimeout(() => {
        if (curr) {
          this.renderer.removeClass(this.loaderEl?.nativeElement, 'hide');
        } else {
          this.renderer.removeClass(this.contentEl?.nativeElement, 'hide');
        }
      }, 0);
    }

    if (prev && !curr) {
      this.queue.push(async () => await this.showContainer());
    } else if (!stateChanges.firstChange && !prev && curr) {
      this.queue.push(async () => await this.showLoader());
    }
  }

  async showContainer() {
    const hideLoader = this.animation
      .create()
      .addElement(this.loaderEl?.nativeElement)
      .duration(200)
      .fromTo('opacity', '1', '0')
      .afterAddClass('hide')
      .easing('ease-out');

    const showContainer = this.animation
      .create()
      .addElement(this.contentEl?.nativeElement)
      .duration(500)
      .beforeRemoveClass('hide')
      .fromTo('opacity', '0', '1')
      .easing('ease-out');

    await hideLoader.play();
    await showContainer.play();
    this.shouldLimitMaxHeight = false;
    this.cdRef.markForCheck();
  }

  async showLoader() {
    // TODO: finally we want to hide the content ASAP -> rework this renderer 2?
    this.shouldLimitMaxHeight = true;
    this.cdRef.markForCheck();

    const showSpinner = this.animation
      .create()
      .addElement(this.loaderEl?.nativeElement)
      .duration(300)
      .beforeRemoveClass('hide')
      .fromTo('opacity', '0', '1')
      .easing('ease-out');

    const hideContainer = this.animation
      .create()
      .addElement(this.contentEl?.nativeElement)
      .duration(0)
      .afterAddClass('hide')
      .fromTo('opacity', '1', '0')
      .easing('ease-out');

    hideContainer.play();
    await showSpinner.play();
  }
}
